import { BotIcon, GitPullRequestIcon, LoaderCircleIcon, PinIcon, TerminalIcon } from "lucide-react";
import type React from "react";
import type { ScopedThreadRef } from "@cmmd-forge/contracts";
import { SidebarMenuSubButton, SidebarMenuSubItem } from "../ui/sidebar";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { formatRelativeTimeLabel } from "../../timestampFormat";
import type { SidebarThreadSummary } from "../../types";
import type { ForgeLinkState } from "../../environments/runtimeSurface";
import type { ThreadOrigin } from "./threadOrigin";
import { useSidebarThreadRow } from "./useSidebarThreadRow";
import { cn } from "~/lib/utils";
import type { SidebarDensity } from "~/uiStateStore";
import {
  describePinAction,
  makePinClickHandler,
  shouldRenderThreadLeadIndicator,
  sidebarStatusDotMotionClass,
} from "./SidebarThreadRow.helpers";
import { synchronizeTerminalPulse, ThreadWorktreeIndicator } from "../ThreadStatusIndicators";
import { SidebarThreadAgentSummary, SidebarThreadWorkerToggle } from "./SidebarThreadWorkerSummary";
import {
  SidebarThreadPullRequestBadge,
  SidebarThreadPullRequestsTooltipSection,
} from "./SidebarThreadPullRequestBadge";
import { SidebarThreadJumpHint, SidebarThreadWorkItemBadge } from "./SidebarThreadRowBadges";
import { SidebarThreadArchiveControl } from "./SidebarThreadArchiveControl";
import { stableTaskAgentName } from "../../lib/taskAgentIdentity";
import { useTaskWorkItemKey } from "./TaskWorkItemKeyContext";
import { archiveAction, workerCount, type ThreadShelfAction } from "./sidebarThreadRowArchive";
const ROW_BUTTON_RENDER = <div role="button" tabIndex={0} />;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface SidebarThreadRowProps {
  readonly thread: SidebarThreadSummary;
  readonly projectCwd: string | null;
  /**
   * The owning project's resolved link state. When provided, thread rows
   * inherit it directly (threads live on the same surface as their project).
   * Null for orphaned threads not yet associated with a project snapshot.
   */
  readonly projectLinkState?: ForgeLinkState | null;
  readonly orderedProjectThreadKeys: readonly string[];
  readonly isActive: boolean;
  readonly jumpLabel: string | null;
  readonly appSettingsConfirmThreadArchive: boolean;
  readonly renamingThreadKey: string | null;
  readonly renamingTitle: string;
  readonly setRenamingTitle: (title: string) => void;
  readonly renamingInputRef: React.MutableRefObject<HTMLInputElement | null>;
  readonly renamingCommittedRef: React.MutableRefObject<boolean>;
  readonly confirmingArchiveThreadKey: string | null;
  readonly setConfirmingArchiveThreadKey: React.Dispatch<React.SetStateAction<string | null>>;
  readonly confirmArchiveButtonRefs: React.MutableRefObject<Map<string, HTMLButtonElement>>;
  readonly handleThreadClick: (
    event: React.MouseEvent,
    threadRef: ScopedThreadRef,
    orderedProjectThreadKeys: readonly string[],
  ) => void;
  readonly navigateToThread: (threadRef: ScopedThreadRef) => void;
  readonly handleMultiSelectContextMenu: (position: { x: number; y: number }) => Promise<void>;
  readonly handleThreadContextMenu: (
    threadRef: ScopedThreadRef,
    position: { x: number; y: number },
  ) => Promise<void>;
  readonly clearSelection: () => void;
  readonly commitRename: (
    threadRef: ScopedThreadRef,
    newTitle: string,
    originalTitle: string,
  ) => Promise<void>;
  readonly cancelRename: () => void;
  readonly attemptArchiveThread: ThreadShelfAction;
  readonly attemptSettleThread: ThreadShelfAction;
  readonly openPrLink: (event: React.MouseEvent<HTMLElement>, prUrl: string) => void;
  // Worker expansion (M4.1 + M4.6) — optional; only rendered for master threads
  readonly workerCount?: number;
  readonly attentionCount?: number;
  readonly workersExpanded?: boolean;
  readonly onToggleWorkers?: (threadKey: string) => void;
  readonly isPinned?: boolean;
  readonly onTogglePinned?: (threadKey: string) => void;
  readonly density?: SidebarDensity;
  /**
   * Additive, optional per-thread execution-origin badge (P3 flag-gated
   * project-grouped sidebar). Undefined for every existing caller — renders
   * nothing, so this is a zero-behavior-change addition to the row.
   */
  readonly environmentBadge?: React.ReactNode;
  /** The machine this thread runs on ("This Device", a cloud VM, a linked host); only the Activity card's details panel reads it — the compact row badges it instead. */
  readonly environmentLabel?: string | null;
  /**
   * WHERE the thread ran, resolved by `resolveThreadOrigin` — the canonical
   * rule, so every surface that marks a location agrees. Replaces a plain
   * `isRemote` boolean, which could not tell a Forge Cloud sprite apart from
   * the user's own Windows box: both wore the same server glyph.
   */
  readonly origin?: ThreadOrigin | undefined;
}

interface SidebarThreadIdentity {
  readonly agentName: string | null;
  readonly title: string;
}

export function resolveSidebarThreadIdentity(thread: SidebarThreadSummary): SidebarThreadIdentity {
  if (thread.kind !== "master" || thread.anchorKind !== "task") {
    return { agentName: null, title: thread.title };
  }
  const agentName = stableTaskAgentName(thread.id);
  return { agentName, title: `${agentName} - ${thread.title}` };
}

/**
 * The title as it reads at rest and while a regeneration is running.
 *
 * A regenerating row dims rather than blanking or shimmering: the old title is
 * still the user's best handle on the thread until the new one lands, and a
 * sidebar full of animating labels is noise. The busy state is announced once
 * for assistive tech, since the visual cue alone does not carry.
 */
export function SidebarThreadTitleLabel(props: {
  readonly identity: SidebarThreadIdentity;
  readonly isRegenerating: boolean;
}) {
  return (
    <span
      className={cn("flex min-w-0 flex-1 items-center gap-1 truncate text-xs", {
        "opacity-55": props.isRegenerating,
      })}
      {...(props.isRegenerating ? { "aria-busy": true } : {})}
    >
      {props.identity.agentName !== null ? (
        <BotIcon
          className="size-3 shrink-0 text-primary"
          aria-label={`${props.identity.agentName}, AI task owner`}
        />
      ) : null}
      <span className="truncate">{props.identity.title}</span>
      {props.isRegenerating ? (
        <span role="status" className="sr-only">
          Regenerating title
        </span>
      ) : null}
    </span>
  );
}

function SidebarThreadTitle(props: {
  readonly identity: SidebarThreadIdentity;
  readonly relativeTime: string;
  readonly renamingTitle: string;
  readonly isRegenerating: boolean;
  readonly state: ReturnType<typeof useSidebarThreadRow>;
  readonly threadId: string;
}) {
  if (props.state.renamingThreadKey === props.state.threadKey) {
    return (
      <input
        ref={props.state.handleRenameInputRef}
        className="min-w-0 flex-1 truncate text-xs bg-transparent outline-none border border-ring rounded px-0.5"
        value={props.renamingTitle}
        onChange={props.state.handleRenameInputChange}
        onKeyDown={props.state.handleRenameInputKeyDown}
        onBlur={props.state.handleRenameInputBlur}
        onClick={props.state.handleRenameInputClick}
      />
    );
  }
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span data-testid={`thread-title-${props.threadId}`} className="flex min-w-0 flex-1">
            <SidebarThreadTitleLabel
              identity={props.identity}
              isRegenerating={props.isRegenerating}
            />
          </span>
        }
      />
      <TooltipPopup side="top" className="max-w-80 whitespace-normal leading-tight">
        {props.identity.title}
        {props.relativeTime ? ` · ${props.relativeTime}` : ""}
        <SidebarThreadPullRequestsTooltipSection entries={props.state.threadPullRequestEntries} />
      </TooltipPopup>
    </Tooltip>
  );
}

type SidebarThreadRowState = ReturnType<typeof useSidebarThreadRow>;

function SidebarThreadTerminalStatus(props: { readonly state: SidebarThreadRowState }) {
  const status = props.state.terminalStatus;
  if (!status) return null;
  return (
    <span
      role="img"
      aria-label={status.label}
      title={status.label}
      className={`inline-flex items-center justify-center ${status.colorClass}`}
    >
      {/* The Activity card's terminal glyph is the same indicator; both use the
          duty-cycled `animate-status-pulse` so one thread does not animate two
          different ways depending on which sidebar layout is showing it.
          `motion-safe:` per DESIGN.md → "Reduced motion is a first-class state". */}
      <TerminalIcon
        className={`size-3 ${status.pulse ? "motion-safe:animate-status-pulse" : ""}`}
        onAnimationStart={synchronizeTerminalPulse}
      />
    </span>
  );
}

function SidebarThreadLeadIndicator(props: {
  readonly handlePinClick: React.MouseEventHandler<HTMLButtonElement>;
  readonly isPinned: boolean;
  readonly pinTooltipLabel: string;
  readonly state: SidebarThreadRowState;
  readonly thread: SidebarThreadSummary;
}) {
  if (props.isPinned) {
    return (
      <Tooltip>
        <TooltipTrigger
          render={
            <button
              type="button"
              data-thread-selection-safe
              data-testid={`thread-pin-${props.thread.id}`}
              aria-label={`Unpin ${props.thread.title}`}
              className="mr-5 inline-flex size-3.5 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
              onPointerDown={props.state.stopPropagationOnPointerDown}
              onClick={props.handlePinClick}
            >
              <PinIcon className="size-3 fill-current" />
            </button>
          }
        />
        <TooltipPopup side="top">{props.pinTooltipLabel}</TooltipPopup>
      </Tooltip>
    );
  }
  if (!shouldRenderThreadLeadIndicator(props.state.leadIndicator)) return null;
  return (
    <span className={props.state.threadMetaClassName}>
      <Tooltip>
        <TooltipTrigger
          render={
            <span
              role="img"
              aria-label={props.state.leadIndicator.tooltip}
              className={`inline-flex items-center justify-center ${props.state.leadIndicator.colorClass}`}
            >
              <SidebarThreadLeadGlyph state={props.state} />
            </span>
          }
        />
        <TooltipPopup side="top">{props.state.leadIndicator.tooltip}</TooltipPopup>
      </Tooltip>
    </span>
  );
}

function SidebarThreadLeadGlyph(props: { readonly state: SidebarThreadRowState }) {
  const indicator = props.state.leadIndicator;
  if (indicator.kind === "working") {
    return <LoaderCircleIcon className="size-3 animate-spin" />;
  }
  if (indicator.kind === "pr-merged") return <GitPullRequestIcon className="size-3" />;
  const motionClass = indicator.pulse
    ? "motion-safe:animate-status-pulse"
    : sidebarStatusDotMotionClass(indicator);
  return (
    <span
      className={`size-[7px] rounded-full ${indicator.dotClass ?? "bg-muted-foreground/50"} ${motionClass}`}
    />
  );
}

function SidebarThreadAvailableMeta(props: {
  readonly handlePinClick: React.MouseEventHandler<HTMLButtonElement>;
  readonly isPinned: boolean;
  readonly pinTooltipLabel: string;
  readonly rowProps: SidebarThreadRowProps;
  readonly state: SidebarThreadRowState;
}) {
  const { state, rowProps } = props;
  const visibleWorkerCount = workerCount(rowProps.thread, rowProps.workerCount);
  const { canArchive, archiveLabel } = archiveAction(rowProps.thread, state.isThreadRunning);
  return (
    <>
      <SidebarThreadTerminalStatus state={state} />
      <SidebarThreadAgentSummary
        workerCount={visibleWorkerCount}
        attentionCount={rowProps.attentionCount ?? 0}
      />
      <div className="group/sidebar-status-slot flex min-w-0 justify-end">
        {canArchive ? (
          <SidebarThreadArchiveControl
            state="available"
            threadId={rowProps.thread.id}
            title={rowProps.thread.title}
            actionLabel={archiveLabel}
            confirmBeforeArchive={rowProps.appSettingsConfirmThreadArchive}
            onPointerDown={state.stopPropagationOnPointerDown}
            onStartConfirmation={state.handleStartArchiveConfirmation}
            onArchiveImmediate={state.handleArchiveImmediateClick}
          />
        ) : null}
        <SidebarThreadPullRequestBadge
          badge={state.linkedPullRequestBadge}
          status={state.linkedPrIndicator}
          onOpenPullRequest={rowProps.openPrLink}
        />
        <SidebarThreadLeadIndicator
          handlePinClick={props.handlePinClick}
          isPinned={props.isPinned}
          pinTooltipLabel={props.pinTooltipLabel}
          state={state}
          thread={rowProps.thread}
        />
      </div>
      <SidebarThreadWorkerToggle
        threadKey={state.threadKey}
        threadId={rowProps.thread.id}
        workerCount={visibleWorkerCount}
        workersExpanded={rowProps.workersExpanded ?? false}
        reserveArchiveSpace={canArchive}
        onToggleWorkers={rowProps.onToggleWorkers}
        onPointerDown={state.stopPropagationOnPointerDown}
      />
    </>
  );
}

function SidebarThreadMeta(props: {
  readonly handlePinClick: React.MouseEventHandler<HTMLButtonElement>;
  readonly isPinned: boolean;
  readonly pinTooltipLabel: string;
  readonly rowProps: SidebarThreadRowProps;
  readonly state: SidebarThreadRowState;
}) {
  if (props.state.isConfirmingArchive) {
    const archiveLabel = props.rowProps.thread.archive?.state === "failed" ? "retry" : "archive";
    return (
      <SidebarThreadArchiveControl
        state="confirming"
        threadId={props.rowProps.thread.id}
        title={props.rowProps.thread.title}
        confirmLabel={`Confirm ${archiveLabel}`}
        confirmButtonRef={props.state.handleConfirmArchiveRef}
        onPointerDown={props.state.stopPropagationOnPointerDown}
        onConfirm={props.state.handleConfirmArchiveClick}
      />
    );
  }
  return <SidebarThreadAvailableMeta {...props} />;
}

// ---------------------------------------------------------------------------
// SidebarThreadRow
// ---------------------------------------------------------------------------

export function SidebarThreadRow(props: SidebarThreadRowProps) {
  const { thread, renamingTitle, isPinned = false, onTogglePinned } = props;
  const s = useSidebarThreadRow(props);
  const resolvedWorkItemKey = useTaskWorkItemKey(thread.anchorCmmdId);
  const handlePinClick = makePinClickHandler(onTogglePinned, s.threadKey);

  const pinTooltipLabel = describePinAction(isPinned);
  const threadIdentity = resolveSidebarThreadIdentity(thread);
  const workItemKey =
    thread.kind === "master" && thread.anchorKind === "task" ? resolvedWorkItemKey : null;

  // Relative timestamp for the title tooltip
  const relativeTime = formatRelativeTimeLabel(
    thread.latestUserMessageAt ?? thread.updatedAt ?? thread.createdAt,
  );

  return (
    <SidebarMenuSubItem
      className="w-full"
      data-thread-item
      onMouseLeave={s.handleMouseLeave}
      onBlurCapture={s.handleBlurCapture}
    >
      <SidebarMenuSubButton
        render={ROW_BUTTON_RENDER}
        size="sm"
        isActive={props.isActive}
        data-testid={`thread-row-${thread.id}`}
        className={s.rowClassName}
        onClick={s.handleRowClick}
        onKeyDown={s.handleRowKeyDown}
        onContextMenu={s.handleRowContextMenu}
      >
        {/* Left: stable task owner + title */}
        <div className="flex min-w-0 flex-1 items-center gap-1.5 text-left">
          <SidebarThreadTitle
            identity={threadIdentity}
            relativeTime={relativeTime}
            renamingTitle={renamingTitle}
            isRegenerating={thread.titleRegeneration != null}
            state={s}
            threadId={thread.id}
          />
        </div>

        {/* Right: status indicator + archive + workers */}
        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          <SidebarThreadWorkItemBadge workItemKey={workItemKey} />
          <ThreadWorktreeIndicator thread={thread} />
          {props.environmentBadge}
          <SidebarThreadMeta
            handlePinClick={handlePinClick}
            isPinned={isPinned}
            pinTooltipLabel={pinTooltipLabel}
            rowProps={props}
            state={s}
          />
        </div>
        <SidebarThreadJumpHint label={props.jumpLabel} threadId={props.thread.id} />
      </SidebarMenuSubButton>
    </SidebarMenuSubItem>
  );
}
